import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AgentSummaryService } from '../../../core/agent/agent-summary.service';
import {
  AgentAttentionItem,
  AgentClientRollup,
  AgentPortfolioSummary,
} from '../../../core/agent/agent-summary.types';
import { AgencyService } from '../../../core/agency/agency.service';
import { Agency } from '../../../core/agency/agency.types';
import { EntityService } from '../../../core/entity/entity.service';
import { AuthService } from '../../../core/auth/auth.service';
import { ENTITY_TYPE_LABELS, Entity } from '../../../core/entity/entity.types';
import { IconComponent, IconName } from '../../../shared/icon/icon';
import { ZarPipe } from '../../../shared/pipes/zar.pipe';

interface Kpi {
  readonly icon: IconName;
  readonly tone: '' | 'success' | 'warning';
  readonly value: string;
  readonly label: string;
  readonly isMoney?: boolean;
  readonly raw?: string;
}

@Component({
  selector: 'app-agent-dashboard',
  imports: [RouterLink, IconComponent, ZarPipe],
  templateUrl: './agent-dashboard.html',
  styleUrl: './agent-dashboard.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentDashboardComponent implements OnInit {
  private readonly summaryService = inject(AgentSummaryService);
  private readonly agencyService = inject(AgencyService);
  private readonly entityService = inject(EntityService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly user = this.authService.currentUser;
  protected readonly summary = signal<AgentPortfolioSummary | null>(null);
  protected readonly entities = signal<Entity[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);

  /** The caller's agency (null for an individual agent). */
  protected readonly agency = signal<Agency | null>(null);
  /** True when the caller is an ADMIN of their agency (can manage the team). */
  protected readonly isAgencyAdmin = computed(() => this.agency()?.member_role === 'ADMIN');
  /** Whether the top-right actions menu is open. */
  protected readonly menuOpen = signal(false);

  protected readonly firstName = computed(() => this.user()?.first_name ?? null);
  protected readonly agencyName = computed(() => this.agency()?.name ?? null);

  protected readonly hasClients = computed(() => this.entities().length > 0);

  protected readonly attention = computed<AgentAttentionItem[]>(
    () => this.summary()?.attention ?? [],
  );

  /** Every in-flight application across the portfolio (newest first). */
  protected readonly live = computed<AgentAttentionItem[]>(() => this.summary()?.live ?? []);

  /** Per-client roll-up keyed by entity short_id for the client cards. */
  protected readonly clientRollup = computed<Record<string, AgentClientRollup>>(() => {
    const out: Record<string, AgentClientRollup> = {};
    for (const c of this.summary()?.clients ?? []) {
      out[c.entity_short_id] = c;
    }
    return out;
  });

  /** Four KPI tiles derived from the portfolio metrics. */
  protected readonly kpis = computed<Kpi[]>(() => {
    const m = this.summary()?.metrics;
    if (!m) return [];
    return [
      { icon: 'building', tone: '', value: String(m.clients), label: 'Linked clients' },
      {
        icon: 'document',
        tone: '',
        value: String(m.live_applications),
        label: 'Live applications',
      },
      {
        icon: 'alert-triangle',
        tone: m.needs_action > 0 ? 'warning' : 'success',
        value: String(m.needs_action),
        label: m.needs_action > 0 ? 'Needs action' : 'All up to date',
      },
      {
        icon: 'wallet',
        tone: 'success',
        value: '',
        raw: m.funding_facilitated,
        isMoney: true,
        label: 'Funding facilitated',
      },
    ];
  });

  protected readonly heroSub = computed(() => {
    const n = this.entities().length;
    if (n === 0) return 'Your linked businesses and their applications appear here.';
    const need = this.summary()?.metrics.needs_action ?? 0;
    if (need > 0) {
      return `${n} client${n === 1 ? '' : 's'} · ${need} need${need === 1 ? 's' : ''} attention`;
    }
    return `${n} client${n === 1 ? '' : 's'} · everything is moving`;
  });

  ngOnInit(): void {
    forkJoin({
      summary: this.summaryService.get(),
      entities: this.entityService.getAgentEntities(),
      // 404 = individual agent (no agency); collapse to null rather than erroring.
      agency: this.agencyService.getMine().pipe(catchError(() => of(null))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ summary, entities, agency }) => {
          this.summary.set(summary);
          this.entities.set(entities.entities);
          this.agency.set(agency);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  protected toggleMenu(): void {
    this.menuOpen.update((v) => !v);
  }

  protected closeMenu(): void {
    this.menuOpen.set(false);
  }

  protected goTo(path: string): void {
    this.menuOpen.set(false);
    void this.router.navigateByUrl(path);
  }

  protected getTypeLabel(type: string): string {
    return ENTITY_TYPE_LABELS[type] ?? type;
  }

  /** A short activity line for a client card; tone drives the dot colour. */
  protected clientStatus(shortId: string): {
    label: string;
    tone: 'attention' | 'positive' | 'neutral';
  } {
    const c = this.clientRollup()[shortId];
    if (c?.needs_action) {
      const n = c.needs_action;
      return { label: `${n} need${n === 1 ? 's' : ''} your action`, tone: 'attention' };
    }
    if (c?.live) {
      const n = c.live;
      return { label: `${n} live application${n === 1 ? '' : 's'}`, tone: 'neutral' };
    }
    if (c?.active) {
      const n = c.active;
      return { label: `${n} active facilit${n === 1 ? 'y' : 'ies'}`, tone: 'positive' };
    }
    return { label: 'No applications yet', tone: 'neutral' };
  }

  protected navigateTo(shortId: string): void {
    void this.router.navigateByUrl(`/entity/${shortId}/dashboard`);
  }

  /** Router link to a single application's progress screen. */
  protected appLink(item: AgentAttentionItem): (string | number)[] {
    // A pre-application has no detail screen of its own yet — it routes back
    // into guided intake on the client entity so the agent can pick up where
    // the applicant left off (answer more-info requests, then resubmit).
    if (item.product === 'PRE') {
      return ['/entity', item.entity_short_id, 'applications', 'new'];
    }
    const product = item.product === 'DF' ? 'df' : 'ks';
    return ['/agent/applications', product, item.short_id];
  }
}
